import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal, TextInput } from '@mantine/core';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import type { ProductView } from '../../../../../services/products/products.service';
import classes from './product-picker-modal.module.scss';

type ProductPickerModalProps = {
  opened: boolean;
  title: string;
  products: ProductView[];
  excludeProductId?: number | null;
  isSubmitting: boolean;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (productIds: number[]) => void;
};

// Architectural summary: Reusable multi-select active-products picker (search + checkboxes).
const ProductPickerModalComponent: React.FC<ProductPickerModalProps> = ({
  opened,
  title,
  products,
  excludeProductId = null,
  isSubmitting,
  confirmLabel,
  onClose,
  onConfirm,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (opened) {
      setSearch('');
      setSelectedIds(new Set());
    }
  }, [opened]);

  const candidateProducts = useMemo(
    () => products.filter((product) => product.isActive && product.id !== excludeProductId),
    [products, excludeProductId],
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filteredProducts = normalizedSearch
    ? candidateProducts.filter((product) => product.name.toLowerCase().includes(normalizedSearch))
    : candidateProducts;

  const toggle = (id: number) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      closeButtonProps={{ disabled: isSubmitting, 'aria-label': 'סגור', 'data-testid': 'product-picker-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <div className={classes.body}>
        <TextInput
          data-testid="product-picker-search"
          placeholder="חיפוש לפי שם"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          onContextMenu={(event) => event.preventDefault()}
          onSelect={(event) => {
            const input = event.currentTarget;
            const end = input.value.length;
            if (input.selectionStart !== end || input.selectionEnd !== end) {
              input.setSelectionRange(end, end);
            }
          }}
          radius="md"
          aria-label="חיפוש מוצרים"
        />
        <div className={classes.list}>
          {filteredProducts.length === 0 ? (
            <div className={classes.empty}>לא נמצאו מוצרים.</div>
          ) : (
            filteredProducts.map((product) => (
              <label key={product.id} className={classes.row} data-testid={`product-picker-row-${product.id}`}>
                <Checkbox
                  checked={selectedIds.has(product.id)}
                  onChange={() => toggle(product.id)}
                  aria-label={product.name}
                />
                <span className={classes.rowName}>{product.name}</span>
              </label>
            ))
          )}
        </div>
        <div className={classes.footer}>
          <span className={classes.count}>{selectedIds.size > 0 ? `${selectedIds.size} נבחרו` : ''}</span>
          <Button variant="cancel" onClick={onClose} disabled={isSubmitting} dataTestId="product-picker-cancel">
            ביטול
          </Button>
          <Button
            variant="save"
            disabled={isSubmitting || selectedIds.size === 0}
            onClick={() => onConfirm(Array.from(selectedIds))}
            dataTestId="product-picker-confirm"
          >
            {isSubmitting ? 'מעתיק…' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const ProductPickerModal = React.memo(ProductPickerModalComponent);
